import React from 'react';
import { SimpleReveal } from './SimpleReveal';
import { GrowthProjectionChart } from './GrowthProjectionChart';
import { Button } from '@/components/ui/button';
import { AnimatedBorderBadge } from './AnimatedBorderBadge';
import { Circle, Pencil, Users, TrendingUp, Grid3x3, RefreshCw, LucideIcon } from 'lucide-react';
interface ArtMeetsScienceFormulaProps {
  className?: string;
}
export function ArtMeetsScienceFormula({
  className = ''
}: ArtMeetsScienceFormulaProps) {
  const principles: Array<{
    title: string;
    description: string;
    side: string;
    icon: LucideIcon;
  }> = [
  // Art Side
  {
    title: "Identity",
    description: "Your identity is what makes you magnetic. We'll help you define what you stand for and how your music and message connect deeply with people.",
    side: "art",
    icon: Circle
  }, {
    title: "Creative Output",
    description: "Every post and video serves a purpose. We'll build content that feels authentic, stops the scroll, and feeds the algorithm without losing your voice.",
    side: "art",
    icon: Pencil
  }, {
    title: "Connection",
    description: "We'll focus on building genuine relationships with your listeners, so your audience becomes a community that grows with you over time.",
    side: "art",
    icon: Users
  },
  // Science Side
  {
    title: "Amplification",
    description: "We'll use data and testing to expand your audience strategically, focusing on the people who are most likely to care about your music.",
    side: "science",
    icon: TrendingUp
  }, {
    title: "Systems",
    description: "Our systems give you structure without stress, keeping everything running smoothly behind the scenes so you can stay focused on creativity.",
    side: "science",
    icon: Grid3x3
  }, {
    title: "Optimization",
    description: "We test everything. Every campaign and post is measured and refined to improve results over time. Small adjustments create lasting growth.",
    side: "science",
    icon: RefreshCw
  }];
  return <section id="framework" data-nav-theme="light" className={`relative bg-white py-8 lg:py-16 ${className}`}>
      {/* Gradient transition from dark to white */}
      <div 
        className="absolute top-0 inset-x-0 h-32 pointer-events-none -mt-32"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.8) 80%, white 100%)'
        }}
      />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Badge & Header */}
        <div className="text-center mb-10">
          <SimpleReveal delay={0}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md mb-4 lg:mb-6">
              <span className="font-mono text-xs font-medium text-[#0071e3] tracking-[0.15em] uppercase">
                &gt; The Artist Engine Formula
              </span>
            </div>
          </SimpleReveal>
          
          <SimpleReveal delay={80}>
            <h2 className="headline-lg text-black text-center mx-auto mb-4">Where Art Meets Science</h2>
          </SimpleReveal>
          
          <SimpleReveal delay={160}>
            <p className="body-large text-black/85 max-w-2xl mx-auto">
              Everyone tells you to <strong>chase numbers</strong>. More followers, more streams, more everything. But numbers without connection <strong>don't convert</strong>—they just create burnout. Real growth happens when creativity and strategy work together.
            </p>
          </SimpleReveal>
        </div>

        {/* Principles Grid with Visual Separation */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-8 md:gap-12 mb-8">
          {/* Art Side - Left Column */}
          <div className="space-y-3 flex flex-col">
            {/* ART Label */}
            <div className="mb-2 py-3">
              <h3 className="mono-text text-2xl font-bold text-black/80 tracking-wider">
                ART
              </h3>
              <p className="body-large text-sm text-black/50 mt-1">
                The creative foundation
              </p>
            </div>
            
            {principles.slice(0, 3).map((principle, index) => <SimpleReveal key={index} delay={index * 80}>
                <div className="bg-black/[0.02] p-4 rounded-lg min-h-[140px] flex flex-col transition-all duration-300 hover:shadow-md hover:scale-[1.01] cursor-pointer">
                  <div className="flex items-start gap-3 flex-1">
                    {/* Icon */}
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                      <principle.icon size={22} strokeWidth={1.5} className="text-[#0071e3]/70" />
                    </div>
                    
                    {/* Content */}
                    <div className="space-y-1.5 flex-1">
                      <h3 className="text-xl font-semibold text-black">{principle.title}</h3>
                      <p className="text-sm text-black/70 leading-relaxed">
                        {principle.description}
                      </p>
                    </div>
                  </div>
                </div>
              </SimpleReveal>)}
          </div>
          
          {/* Vertical Divider - Hidden on Mobile */}
          <div className="hidden md:block w-px bg-black/5" />
          
          {/* Science Side - Right Column */}
          <div className="space-y-3 flex flex-col">
            {/* SCIENCE Label */}
            <div className="mb-2 py-3">
              <h3 className="mono-text text-2xl font-bold text-black/80 tracking-wider">
                SCIENCE
              </h3>
              <p className="body-large text-sm text-black/50 mt-1">
                The strategic amplifier
              </p>
            </div>
            
            {principles.slice(3).map((principle, index) => <SimpleReveal key={index + 3} delay={(index + 3) * 80}>
                <div className="bg-black/[0.02] p-4 rounded-lg min-h-[140px] flex flex-col transition-all duration-300 hover:shadow-md hover:scale-[1.01] cursor-pointer">
                  <div className="flex items-start gap-3 flex-1">
                    {/* Icon */}
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                      <principle.icon size={22} strokeWidth={1.5} className="text-[#0071e3]/70" />
                    </div>
                    
                    {/* Content */}
                    <div className="space-y-1.5 flex-1">
                      <h3 className="text-xl font-semibold text-black">{principle.title}</h3>
                      <p className="text-sm text-black/70 leading-relaxed">
                        {principle.description}
                      </p>
                    </div>
                  </div>
                </div>
              </SimpleReveal>)}
          </div>
        </div>

        {/* Closing CTA - Elevated Container */}
        <div className="relative mt-24 mb-12">
          <SimpleReveal delay={500}>
            <div className="relative max-w-7xl mx-auto">
              {/* Breathing Glow Effect */}
              <div 
                className="absolute -inset-[2px] rounded-[32px] opacity-40"
                style={{
                  background: 'radial-gradient(circle at center, rgba(0,113,227,0.15), transparent 70%)',
                  filter: 'blur(8px)'
                }}
              />
              
          {/* Clean Container */}
          <div 
            id="growth-projection" data-nav-theme="dark"
            className="relative overflow-hidden border border-white/10 rounded-[32px] px-4 lg:px-12 py-16 shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
            style={{
              background: 'radial-gradient(ellipse at center, hsl(0, 0%, 6%) 0%, hsl(0, 0%, 3%) 70%)'
            }}
          >
            <div className="space-y-6 text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md shadow-[0_0_20px_rgba(0,113,227,0.3)] md:shadow-none transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] md:hover:bg-[#0071e3]/15 md:hover:border-[#0071e3]/35 md:hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
                <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
                  &gt; Ready to Apply
                </span>
              </div>
              
              {/* Headline + Subheader grouped to bypass parent space-y */}
              <div className="flex flex-col items-center gap-y-3">
                <h3 className="headline-lg text-white">
                  Built For Artists Ready To Scale
                </h3>
                <p className="text-base lg:text-lg text-white/70 max-w-2xl mx-auto font-light">
                  Predictable growth. Real fans. Zero guesswork.
                </p>
              </div>
              
              {/* Growth Chart */}
              <SimpleReveal delay={320}>
                <div className="my-10">
                  <GrowthProjectionChart />
                </div>
              </SimpleReveal>
              
              <p className="text-sm lg:text-base text-white/85 max-w-xl mx-auto">
                We're application-only so we can focus on artists who are serious about building momentum. If that's you, we'd love to hear your story.
              </p>
                  
                  {/* CTA Button - Black with subtle border */}
                  <div className="inline-block group shadow-[0_0_16px_rgba(0,113,227,0.20)] md:shadow-[0_0_30px_rgba(0,113,227,0.35)] md:rounded-xl">
                    <Button 
                      variant="hero-black-ring" 
                      size="hero" 
                      className="w-auto relative border border-[#0071e3]/40 shadow-[0_0_14px_rgba(0,113,227,0.18)] hover:border-[#0071e3]/60 hover:shadow-[0_0_24px_rgba(0,113,227,0.35)] transition-all duration-300 hover:scale-[1.02]"
                      asChild
                    >
                      <a href="https://a23j5fry30t.typeform.com/to/CzjaFT5z" target="_blank" rel="noopener noreferrer">
                      Apply Now
                      </a>
                    </Button>
                  </div>
                  <p className="text-xs text-white/50 mt-4 font-light tracking-wide">
                    All music listened to by humans, not AI
                  </p>
                </div>
              </div>
            </div>
          </SimpleReveal>
        </div>
      </div>
    </section>;
}